<template>
  <v-card rounded elevation="2" class="mb-6">
    <div class="root-cause__header pa-5 d-flex justify-between align-center">
      <span class="body-2">
        {{ title }}
      </span>

      <Button
        v-if="isErasable && isEditMode"
        icon="mdi-close"
        color="grey"
        :data-testid="`${tid}-button-delete`"
        @click="handleDeleteRootCause"
      />
    </div>
    <FiveWhysToolModal
      v-if="showFiveWhysTool"
      :show="showFiveWhysTool"
      :isEditMode="isEditMode"
      :rootCause="rootCause"
      @close="closeFiveWhysTool"
      @setAnalysisWhyBreakdown="setAnalysisWhyBreakdown"
      @updateRootCause="updateRootCause"
    />

    <v-card-text
      class="pt-0 mt-0"
      :class="{ 'pb-2': isEditMode, 'pb-3': !isEditMode }"
    >
      <template v-if="rootCause.name || isEditMode">
        <v-row>
          <v-col class="text-right ">
            <a
              v-if="canUseFiveWhysTool"
              class="caption d-flex justify-end"
              data-tourid="problemSolving-5whys-tool-button"
              @click.prevent="openFiveWhysTool"
            >
              {{ $t('problem_solving.analyse_tool.5w_tool_name') }}</a
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="rootCause.description || isEditMode">
            <TextQuestion
              data-private
              class="root-cause__why-textarea"
              rows="6"
              :class="{ 'mt-2': isEditMode }"
              :tourId="`${tid}-textField-analysis`"
              :maxLength="$constants.problemSolving.rootCauseMaxLength"
              :value="rootCauseDescription"
              :question="
                $t('problem_solving.problem_analysis.root_cause_analysis')
              "
              :placeholder="$t('problem_solving.root_causes.why_placeholder')"
              :isEditMode="isEditMode"
              :tid="`${tid}-text-why`"
              @input="handleDescriptionChange"
              @focusOut="trackEvent('rootCauseWhy')"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <TextField
              data-private
              :tourId="`${tid}-input-name`"
              :label="`${$t('problem_solving.root_causes.root_cause_name')}*`"
              :placeholder="
                $t('problem_solving.root_causes.root_cause_name_placeholder')
              "
              :isReadOnly="!isEditMode"
              :value="rootCause.name"
              :error="markRequiredFields && !rootCause.name"
              :testId="`${tid}-input-name`"
              @input="handleNameChange"
              @focusOut="trackEvent('rootCauseName')"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="rootCause.type || isEditMode">
            <SelectField
              data-private
              :tourId="`${tid}-select-type`"
              :items="rootCauseTypes"
              :isReadOnly="!isEditMode"
              :label="$t('common.type')"
              :placeholder="$t('common.select')"
              :tid="`${tid}-select-type`"
              :value="rootCause.type"
              @input="handleTypeChange"
            />
          </v-col>
        </v-row>
      </template>
      <div class="d-flex justify-space-between align-center" v-else>
        <v-chip
          v-if="!rootCause.description && !isEditMode"
          disabled
          small
          class="label mt-2"
          >{{ $t('common.in_progress_status') }}</v-chip
        >
        <a
          v-if="canUseFiveWhysTool"
          class="caption"
          data-tourid="problemSolving-5whys-tool-button"
          @click.prevent="openFiveWhysTool"
        >
          {{ $t('problem_solving.analyse_tool.5w_tool_name') }}</a
        >
      </div>
      <p class="d-flex justify-end caption blue-grey--text">
        * {{ $t('common.required_fields') }}
      </p>
    </v-card-text>
  </v-card>
</template>

<script>
import FiveWhysToolModal from '@/containers/ProblemSolvingView/FiveWhysTool/FiveWhysToolModal.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import TextField from '@/components/Core/TextField.vue'
import SelectField from '@/components/Core/SelectField.vue'
import Button from '@/components/UI/Button'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

export default {
  name: 'RootCause',

  components: {
    FiveWhysToolModal,
    TextField,
    SelectField,
    TextQuestion,
    Button,
  },

  mixins: [trackerMixin],

  props: {
    title: {
      type: String,
      default: '',
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    rootCause: {
      type: Object,
      required: true,
    },
    isErasable: {
      type: Boolean,
      default: false,
    },
    tid: {
      type: String,
      default: 'root-cause',
    },
    markRequiredFields: Boolean,
  },
  data() {
    return {
      rootCauseTypes: this.$constants.problemSolving.rootCauseTypes.map(
        type => ({
          value: type.value,
          text: this.$t(type.name),
        })
      ),
      showFiveWhysTool: false,
    }
  },

  computed: {
    rootCauseDescription() {
      return this.rootCause?.description ?? ''
    },

    canUseFiveWhysTool() {
      return Boolean(
        this.isEditMode || this.rootCause?.analysisWhyBreakdown?.length
      )
    },
  },

  methods: {
    updateRootCause(rootCause) {
      this.$emit('rootCauseChange', {
        ...this.rootCause,
        ...rootCause,
      })
    },

    setAnalysisWhyBreakdown(analysisWhyBreakdown) {
      this.handleRootCauseChange('analysisWhyBreakdown', analysisWhyBreakdown)
    },

    openFiveWhysTool() {
      this.showFiveWhysTool = true
      this.trackTool('5Whys', 'launch', 'problem-solving')
    },

    closeFiveWhysTool() {
      this.showFiveWhysTool = false
      this.trackTool('5Whys', 'close', 'problem-solving')
    },

    handleRootCauseChange(field, value) {
      this.$emit('rootCauseChange', {
        ...this.rootCause,
        [field]: value,
      })
    },

    handleNameChange(name) {
      this.handleRootCauseChange('name', name)
    },

    handleTypeChange(type) {
      this.trackEvent('rootCauseType')
      this.handleRootCauseChange('type', type)
    },

    handleDescriptionChange(description) {
      this.handleRootCauseChange('description', description)
    },

    handleDeleteRootCause() {
      if (!this.isErasable) return
      this.$emit('rootCauseDelete')
    },

    trackEvent: function(changedField) {
      this.$emit('trackEvent', changedField)
    },
  },
}
</script>

<style lang="scss" scoped>
.root-cause {
  &__container {
    box-shadow: 0px 1px 2px rgba(120, 135, 155, 0.3);
    border-radius: 4px;
  }

  &__content {
    padding: 20px;
  }
}
</style>
